import { db, functions } from '../firebase';
import {
  collection, doc, setDoc, getDocs,
  query, orderBy, limit, serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { AdminAction, FieldDelta, UserRole } from '../types/user';

const COLLECTION = 'adminActions';

/**
 * Log an admin action as a set of field deltas.
 * Each entry in `changes` is one changed field: { from: oldVal, to: newVal }.
 * Rollback = re-apply all `from` values. The delta chain supports incremental undo.
 */
export async function logAdminAction(params: {
  actorId: string;
  actorName: string;
  actorRole: UserRole;
  action: string;
  actionLabel: string;
  targetType: string;
  targetCollection: string;
  targetId: string;
  targetLabel?: string | null;
  changes: Record<string, FieldDelta>;
  rollbackable?: boolean;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const ref = doc(collection(db, COLLECTION));
  await setDoc(ref, {
    id: ref.id,
    actorId: params.actorId,
    actorName: params.actorName,
    actorRole: params.actorRole,
    action: params.action,
    actionLabel: params.actionLabel,
    targetType: params.targetType,
    targetCollection: params.targetCollection,
    targetId: params.targetId,
    targetLabel: params.targetLabel ?? null,
    changes: params.changes,
    rollbackable: params.rollbackable ?? true,
    rolledBackAt: null,
    rolledBackBy: null,
    rolledBackByName: null,
    metadata: params.metadata ?? {},
    createdAt: new Date().toISOString(),
    _createdAt: serverTimestamp(),
  });
}

/**
 * Fetch all admin actions, newest first.
 */
export async function getAllAdminActions(limitCount = 300): Promise<AdminAction[]> {
  const q = query(
    collection(db, COLLECTION),
    orderBy('_createdAt', 'desc'),
    limit(limitCount),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as AdminAction);
}

/**
 * Roll back an action via Cloud Function (uses Admin SDK to bypass Firestore rules).
 * The CF applies the inverse deltas and logs a new rollback action so the
 * chain can continue to be unwound further.
 */
export async function rollbackAdminAction(actionId: string): Promise<void> {
  const fn = httpsCallable(functions, 'adminRollbackAction');
  await fn({ actionId });
}
